import { API_BASE_URL, api } from './client'
import type { components } from './generated/schema'

/** Camera độc lập; `grower_area` là vùng trồng đang gắn (null = chưa gắn). */
export type Camera = components['schemas']['Camera']
export type CameraGrowerArea = components['schemas']['CameraGrowerArea']
export type CameraIrrigationDevice =
    components['schemas']['CameraIrrigationDevice']
export type SaveCameraRequest =
    components['schemas']['SaveGrowerAreaCameraRequest']
export type CreateCameraRequest = components['schemas']['CreateCameraRequest']
export type CameraControlRequest =
    components['schemas']['GrowerAreaCameraControlRequest']
export type CameraTestResult =
    components['schemas']['GrowerAreaCameraTestResult']

const base = '/cameras'

export const camerasApi = {
    /** Mọi camera; có `growerAreaId` thì chỉ camera của vùng đó, theo thứ tự lưới. */
    list: (growerAreaId?: string) =>
        api.get<Camera[]>(base, { params: { grower_area_id: growerAreaId } }),

    /** Camera của một trạm bơm, theo thứ tự trong trạm. */
    listByIrrigationDevice: (deviceId: string) =>
        api.get<Camera[]>(base, { params: { irrigation_device_id: deviceId } }),

    create: (data: CreateCameraRequest) => api.post<Camera>(base, data),

    update: (cameraId: string, data: SaveCameraRequest) =>
        api.put<Camera>(`${base}/${cameraId}`, data),

    remove: (cameraId: string) =>
        api.delete<{ message: string }>(`${base}/${cameraId}`),

    /** Gắn camera vào vùng (chuyển vùng nếu đang ở vùng khác); null = gỡ khỏi vùng. */
    setLocation: (cameraId: string, growerAreaId: string | null) =>
        api.put<Camera>(`${base}/${cameraId}/location`, {
            grower_area_id: growerAreaId,
        }),

    /** Gắn camera vào trạm bơm (giữ các nơi đang gắn). */
    attachIrrigationDevice: (cameraId: string, deviceId: string) =>
        api.put<Camera>(
            `${base}/${cameraId}/irrigation-devices/${encodeURIComponent(deviceId)}`,
            {},
        ),

    detachIrrigationDevice: (cameraId: string, deviceId: string) =>
        api.delete<Camera>(
            `${base}/${cameraId}/irrigation-devices/${encodeURIComponent(deviceId)}`,
        ),

    control: (cameraId: string, data: CameraControlRequest) =>
        api.post<CameraControlRequest>(`${base}/${cameraId}/control`, data),

    test: (cameraId: string) =>
        api.post<CameraTestResult>(`${base}/${cameraId}/test`, {}),
}

/**
 * URL MỘT khung hình tĩnh, để gắn vào `<img src>` của thẻ camera.
 *
 * Thẻ trong lưới dùng đường này thay vì luồng trực tiếp: mỗi luồng sống là
 * một dòng băng thông liên tục (khoảng 3 Mbps) kéo từ vườn, nhân với số camera
 * và số người đang mở màn. `refreshToken` đổi mỗi lần làm mới
 * để trình duyệt không trả lại ảnh cũ trong cache.
 */
export function cameraSnapshotUrl(
    cameraId: string,
    refreshToken: string | number,
): string {
    return `${API_BASE_URL}${base}/${cameraId}/snapshot?t=${refreshToken}`
}

/**
 * URL playlist HLS gốc của camera, để trình phát `hls.js` (hoặc HLS sẵn có của
 * Safari) mở luồng trực tiếp.
 *
 * Mọi request tiếp theo của trình phát (playlist con, đoạn video) cũng đi qua
 * relay của ERP — playlist đã được server viết lại thành đường tương đối dưới
 * `live/`. Xác thực bằng cookie `access_token`; tài khoản Basic Auth của
 * camera nằm ở server, không bao giờ xuống trình duyệt.
 */
export function cameraLiveUrl(cameraId: string): string {
    return `${API_BASE_URL}${base}/${cameraId}/live/index.m3u8`
}
